import {Injectable, inject} from '@angular/core';
import {Subject} from 'rxjs';
import {AuthService} from '../auth/auth.service';
import {SearchIntent} from '../shared/scanning/code-scan';
import {GlobalSearchVisibilityMode} from '../shared/search/GlobalSearchVisibilityMode';
import {defaultSearchFilters} from './SearchFilters';
import {SearchState} from './SearchState';

@Injectable({providedIn: 'root'})
export class SearchStateService {
  private readonly auth = inject(AuthService);
  readonly entered = new Subject<void>();
  private memory: {key: string; state: SearchState} | null = null;
  private get key(): string { return `invemory.search.v1.${this.auth.session?.user.id ?? ''}`; }

  read(): SearchState {
    const fallback: SearchState = {query: '', intent: 'text', filters: defaultSearchFilters(), itemPages: 1, containerPages: 1, scrollTop: 0};
    if (this.memory?.key === this.key) return structuredClone(this.memory.state);
    try {
      const parsed = JSON.parse(sessionStorage.getItem(this.key) ?? 'null') as SearchState | null;
      return this.valid(parsed) ? {...fallback, query: parsed!.query, intent: parsed!.intent, filters: parsed!.filters} : fallback;
    } catch { return fallback; }
  }

  private valid(state: SearchState | null): boolean {
    if (!state || typeof state.query !== 'string' || !state.filters) return false;
    return ['text', 'qr', 'barcode'].includes(state.intent)
      && ['all', 'items', 'containers'].includes(state.filters.resultType)
      && ['all', 'withZeroCount', 'withoutZeroCount'].includes(state.filters.visibility)
      && typeof state.filters.containerId === 'string';
  }

  save(state: SearchState): void {
    this.memory = {key: this.key, state: structuredClone(state)};
    try { sessionStorage.setItem(this.key, JSON.stringify({query: state.query, intent: state.intent, filters: state.filters})); }
    catch { /* In-memory navigation remains available without browser storage. */ }
  }

  enter(query: string, visibility: GlobalSearchVisibilityMode, intent: SearchIntent): void {
    this.save({query, intent, filters: {...defaultSearchFilters(), visibility}, itemPages: 1, containerPages: 1, scrollTop: 0});
    this.entered.next();
  }
}
